import React, { useMemo } from 'react';
import { getMonthGridDays } from '../../utils/calendarUtils';

// Label the per-day amount with its correct unit. Hours are shown to one
// decimal ("7.5 hrs"); days are shown compactly ("1 day" / "0.5 days") since a
// day-unit leave reading as "1.0 hrs" is what we're fixing here.
const formatAmount = (amount = 0, unit = 'Days') => {
  if (unit === 'Hours') {
    return `${amount.toFixed(1)} hrs`;
  }
  const dayValue = Number.isInteger(amount) ? String(amount) : amount.toFixed(1);
  return `${dayValue} day${amount === 1 ? '' : 's'}`;
};

// Same status -> color mapping as the day cells on the Calendar page
// (ManagerCalendar.jsx / EmployeeCalendar.jsx getDayStyle), applied to this
// employee's leave entries instead of the signed-in user's own leave.
const getDayStyle = (leavesByDate, dateStr) => {
  const leaves = leavesByDate[dateStr];
  if (!leaves || leaves.length === 0) {
    return { bg: 'bg-white border-gray-200', text: 'text-gray-400', label: '' };
  }

  const s = leaves[0].status;
  const label = leaves.length > 1 ? `${leaves[0].type} +${leaves.length - 1}` : leaves[0].type;

  if (s === 'Approved' || s === 'Completed')
    return { bg: 'bg-green-100 border-green-300', text: 'text-green-800', label };
  if (s === 'Pending' || s === 'Submitted' || s === 'Draft' || s === 'InReview')
    return { bg: 'bg-yellow-100 border-yellow-300', text: 'text-yellow-800', label };
  if (s === 'Rejected')
    return { bg: 'bg-red-100 border-red-300', text: 'text-red-800', label };
  if (s === 'Cancelled' || s === 'Canceled')
    return { bg: 'bg-gray-100 border-gray-300', text: 'text-gray-800', label };
  return { bg: 'bg-gray-100 border-gray-300', text: 'text-gray-700', label };
};

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Month-grid calendar for a single team member's leave entries, styled to
// match the Calendar page (ManagerCalendar.jsx) day-cell look exactly.
const CalendarRow = ({ entries, month, onSelectEntry }) => {
  const monthGridDays = useMemo(() => getMonthGridDays(month), [month]);

  const leavesByDate = useMemo(() => {
    const map = {};
    entries.forEach((entry) => {
      if (!map[entry.date]) map[entry.date] = [];
      map[entry.date].push(entry);
    });
    return map;
  }, [entries]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        {dayNames.map((day) => (
          <div
            key={day}
            className="p-1.5 text-center text-[10px] sm:text-xs font-medium text-gray-600 border-r last:border-r-0 border-gray-200"
          >
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {monthGridDays.map((dayInfo, index) => {
          if (!dayInfo) {
            return (
              <div
                key={`empty-${index}`}
                className="min-h-[52px] sm:min-h-[64px] p-1 bg-gray-50 border-r border-b last:border-r-0 border-gray-200"
              />
            );
          }
          const style = getDayStyle(leavesByDate, dayInfo.date);
          const leaves = leavesByDate[dayInfo.date];
          const primaryEntry = leaves?.[0];
          return (
            <button
              key={dayInfo.date}
              type="button"
              disabled={!primaryEntry}
              onClick={() => primaryEntry && onSelectEntry?.(primaryEntry)}
              title={
                primaryEntry
                  ? `${primaryEntry.type} — ${formatAmount(primaryEntry.amount, primaryEntry.unit)} (${primaryEntry.status})`
                  : undefined
              }
              className={`min-h-[52px] sm:min-h-[64px] p-1 text-left border-r border-b last:border-r-0 border-gray-200 ${style.bg} transition-colors ${
                primaryEntry ? 'hover:opacity-90 cursor-pointer' : 'cursor-default'
              }`}
            >
              <div className="flex flex-col h-full">
                <span
                  className={`text-xs font-medium ${
                    dayInfo.isToday
                      ? 'bg-indigo-600 text-white w-5 h-5 rounded-full flex items-center justify-center'
                      : style.text
                  }`}
                >
                  {dayInfo.day}
                </span>
                {style.label && (
                  <span className={`mt-0.5 text-[9px] sm:text-[10px] font-medium ${style.text} block truncate`}>
                    {style.label}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarRow;
